/**
 * Hidden Listings — per-user localStorage bucket.
 *
 * Hardened against cross-user data leakage (same pattern as Recently Viewed):
 *   • ALL reads/writes require a real, non-empty userId. An empty string,
 *     null, undefined, or the sentinel `"guest"` used by anonymous pages
 *     are REJECTED — no-ops on writes, [] on reads. There is no unscoped
 *     fallback key any more.
 *   • Bucket key is versioned: `wassla_hidden_listings_v2_<userId>`.
 *   • `sweepLegacyHiddenListingKeys()` removes every pre-v2 bucket
 *     (shared + per-user). `purgeAllHiddenListings()` nukes even v2
 *     buckets — used on logout.
 *
 * Anonymous users do NOT persist hidden listings to disk; an anonymous
 * hide is simply a no-op. This is intentional per product rules.
 */

const PREFIX_V2 = 'wassla_hidden_listings_v2_';
const LEGACY_PREFIX_V1 = 'wassla_hidden_listings_';
const LEGACY_SHARED_KEY = 'wassla_hidden_listings';

function isValidUserId(userId) {
  if (typeof userId !== 'string') return false;
  const trimmed = userId.trim();
  if (!trimmed) return false;
  // Reject the shared anonymous sentinel used by pages that still need
  // a key for other (unrelated) features.
  if (trimmed === 'guest') return false;
  return true;
}

function getKey(userId) {
  return isValidUserId(userId) ? `${PREFIX_V2}${userId}` : null;
}

export function sweepLegacyHiddenListingKeys() {
  try {
    localStorage.removeItem(LEGACY_SHARED_KEY);
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(LEGACY_PREFIX_V1) && !k.startsWith(PREFIX_V2)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* storage unavailable — ignore */
  }
}

export function purgeAllHiddenListings() {
  try {
    sweepLegacyHiddenListingKeys();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX_V2)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* storage unavailable — ignore */
  }
}

export function hideListing(adId, userId) {
  if (!adId || !isValidUserId(userId)) return;
  const key = getKey(userId);
  if (!key) return;
  try {
    const set = new Set(getHiddenListingIds(userId));
    set.add(adId);
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

export function unhideListing(adId, userId) {
  if (!adId || !isValidUserId(userId)) return;
  const key = getKey(userId);
  if (!key) return;
  try {
    const set = new Set(getHiddenListingIds(userId));
    set.delete(adId);
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

export function getHiddenListingIds(userId) {
  if (!isValidUserId(userId)) return [];
  const key = getKey(userId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((id) => typeof id === 'string' && id)
      : [];
  } catch {
    return [];
  }
}

export function isListingHidden(adId, userId) {
  return getHiddenListingIds(userId).includes(adId);
}
